import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Ban, Plus, ChevronRight, Receipt } from 'lucide-react';
import type { GymPaymentRow } from '@techsofit/api-types';
import { useAuth } from '@techsofit/app-kit';
import { Button, Card, Skeleton, EmptyState, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { VoidPaymentModal } from '../components/VoidPaymentModal';
import { listAllPayments, voidPayment } from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { money, formatDate, todayIso, addCalendarDays } from '../lib/format';

/** What the payment was for — spelled out, never "PT". */
function TypeTag({ type }: { type: GymPaymentRow['type'] }) {
  const isPt = type === 'PERSONAL_TRAINING';
  return (
    <span
      className={cn(
        'shrink-0 rounded-pill px-2 py-0.5 text-xs font-medium',
        isPt ? 'bg-accent-soft text-ink' : 'bg-surface text-ink-soft',
      )}
    >
      {isPt ? 'Personal training' : 'Membership'}
    </span>
  );
}

type Filter = 'ALL' | 'MEMBERSHIP' | 'RENEWAL' | 'PERSONAL_TRAINING' | 'VOIDED';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'MEMBERSHIP', label: 'Membership' },
  { value: 'RENEWAL', label: 'Renewals' },
  { value: 'PERSONAL_TRAINING', label: 'Personal training' },
  { value: 'VOIDED', label: 'Voided' },
];

function isoDay(deltaDays = 0): string {
  return addCalendarDays(todayIso(), deltaDays);
}
function monthStartIso(): string {
  return `${todayIso().slice(0, 7)}-01`;
}

type DateRange = { label: string; from: string; to: string };
const DATE_PRESETS: DateRange[] = [
  { label: 'All time', from: '', to: '' },
  { label: 'Today', from: isoDay(0), to: isoDay(0) },
  { label: 'This month', from: monthStartIso(), to: isoDay(0) },
];

export function PaymentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === 'GYM_OWNER';

  const { data, loading, error, reload } = useAsync(listAllPayments);
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  // Deep-link support: the dashboard cards link here with ?type= and ?from&to.
  const initialType = searchParams.get('type');
  const [filter, setFilter] = useState<Filter>(
    initialType && FILTERS.some((f) => f.value === initialType) ? (initialType as Filter) : 'ALL',
  );
  const [from, setFrom] = useState(searchParams.get('from') ?? '');
  const [to, setTo] = useState(searchParams.get('to') ?? '');
  const [voidingId, setVoidingId] = useState<string | null>(null);
  const [voidTarget, setVoidTarget] = useState<GymPaymentRow | null>(null);

  const activePreset = useMemo(
    () => DATE_PRESETS.find((p) => p.from === from && p.to === to)?.label ?? 'Custom',
    [from, to],
  );

  const payments = useMemo(() => data ?? [], [data]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return payments.filter((p) => {
      if (filter === 'VOIDED' && !p.voided) return false;
      if (filter === 'MEMBERSHIP' && p.type !== 'MEMBERSHIP') return false;
      if (filter === 'RENEWAL' && !p.isRenewal) return false;
      if (filter === 'PERSONAL_TRAINING' && p.type !== 'PERSONAL_TRAINING') return false;
      if (from && p.paidOn < from) return false;
      if (to && p.paidOn > to) return false;
      if (q && !p.memberName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [payments, filter, query, from, to]);

  // Collected = non-voided in the current view.
  const collected = filtered.filter((p) => !p.voided).reduce((sum, p) => sum + Number(p.amount), 0);

  async function doVoid(reason: string) {
    const p = voidTarget;
    if (!p) return;
    setVoidingId(p.id);
    try {
      await voidPayment(p.id, reason);
      await reload();
      setVoidTarget(null);
    } finally {
      setVoidingId(null);
    }
  }

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={`${payments.length} payment${payments.length === 1 ? '' : 's'}`}
        title="Payments"
        description="Every payment across your business — membership and personal training, voided and not."
        actions={
          <Button onClick={() => navigate('/payments/new')}>
            <Plus className="size-4" aria-hidden />
            <span className="hidden sm:inline">Record payment</span>
          </Button>
        }
      />

      {/* Search + filters */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            type="search"
            inputMode="search"
            placeholder="Search by member…"
            aria-label="Search payments by member"
            className="h-touch w-full rounded-control border border-line bg-elevated pl-10 pr-4 text-ink placeholder:text-muted focus:border-ink-soft"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              aria-pressed={filter === f.value}
              className={cn(
                'rounded-pill px-3 py-1.5 text-sm font-medium transition-colors',
                filter === f.value ? 'bg-ink text-on-dark' : 'bg-surface text-ink-soft hover:bg-line-strong',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Date range */}
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                setFrom(p.from);
                setTo(p.to);
              }}
              className={cn(
                'rounded-pill px-3 py-1.5 text-sm font-medium transition-colors',
                activePreset === p.label ? 'bg-dark text-on-dark' : 'bg-card text-ink-soft hover:bg-surface',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs text-ink-soft">
            From
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 block rounded-control border border-line px-2.5 py-1.5 text-sm text-ink"
            />
          </label>
          <label className="text-xs text-ink-soft">
            To
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 block rounded-control border border-line px-2.5 py-1.5 text-sm text-ink"
            />
          </label>
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="rounded-control px-2 py-1.5 text-sm text-muted hover:text-ink"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-80" />
      ) : error ? (
        <EmptyState title="Couldn’t load payments" description={error} />
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between border-b border-line px-5 py-3 text-sm">
            <span className="text-muted">
              {filtered.length} shown{filter === 'VOIDED' ? '' : ` · ${money(collected)} collected`}
              {activePreset !== 'All time' && ` · ${activePreset.toLowerCase()}`}
            </span>
            <Receipt className="size-4 text-muted" aria-hidden />
          </div>
          {filtered.length ? (
            <ul className="divide-y divide-line">
              {filtered.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-4 py-3 md:px-5">
                  <button
                    type="button"
                    onClick={() => navigate(`/members/${p.memberId}`)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 truncate font-medium text-ink">
                        <span className="truncate">{p.memberName}</span>
                        <TypeTag type={p.type} />
                        {p.isRenewal && (
                          <span className="shrink-0 rounded-pill bg-ok-soft px-2 py-0.5 text-xs font-medium text-ok-ink">
                            Renewal
                          </span>
                        )}
                      </p>
                      <p className="truncate text-xs text-muted tabular">
                        {formatDate(p.paidOn)} · {p.method}
                        {p.type === 'PERSONAL_TRAINING'
                          ? p.coachName
                            ? ` · ${p.coachName}`
                            : ''
                          : p.periodEnd
                            ? ` · until ${formatDate(p.periodEnd)}`
                            : ''}
                      </p>
                      {p.voided && p.voidReason && (
                        <p className="truncate text-xs text-bad-ink">Voided: {p.voidReason}</p>
                      )}
                    </div>
                    <span className={cn('shrink-0 font-medium tabular', p.voided ? 'text-muted line-through' : 'text-ink')}>
                      {money(p.amount)}
                    </span>
                    <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />
                  </button>
                  {p.voided ? (
                    <span className="shrink-0 rounded-pill bg-bad-soft px-2 py-0.5 text-xs font-medium text-bad-ink">Voided</span>
                  ) : isOwner ? (
                    <button
                      type="button"
                      onClick={() => setVoidTarget(p)}
                      disabled={voidingId === p.id}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-medium text-ink-soft transition-colors hover:bg-bad-soft hover:text-bad-ink disabled:opacity-50"
                    >
                      <Ban className="size-3.5" aria-hidden />
                      {voidingId === p.id ? 'Voiding…' : 'Void'}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              className="rounded-none shadow-none"
              title={payments.length ? 'No matching payments' : 'No payments yet'}
              description={payments.length ? 'Try a different filter or search.' : 'Record your first payment to see it here.'}
            />
          )}
        </Card>
      )}

      {voidTarget && (
        <VoidPaymentModal
          description={`${voidTarget.memberName} · ${money(voidTarget.amount)}. It stays in the list (struck-through) but no longer counts, and their membership/PT is recalculated.`}
          onClose={() => setVoidTarget(null)}
          onConfirm={doVoid}
        />
      )}
    </section>
  );
}
